/* eslint-disable jsx-a11y/anchor-is-valid */

import { FC, useMemo } from 'react'

import { useFormik } from 'formik'
import { vincularPacienteEmpresa } from '../../core/_requests'
import {
  ButtonType,
  GeneroType,
  OptionsHelper,
  RacaType,
  showAlert,
  showError,
} from '../../../../../../../_metronic/helpers'
import {
  Button,
  CustomAccordion,
  FormInput,
  FormSelect,
  FormTextArea,
  IOption,
  Label,
  LoadingIndicator,
  useModal,
} from '../../../../../../components'
import {
  PacienteDtoAdd,
  PacienteDtoDadosAdicionais,
  PacienteDtoDadosSaude,
} from '../../core/_models'
import { updatePacienteSchema } from './validation/updatePacienteSchema'
import { IDocumento } from '../../../../../../components/Modal/SearchPersonModal'
import { useT } from '_metronic/i18n/Metronici18n'

type Props = {
  documento: IDocumento
  nome: string
}

export const PacienteProntuarioModal: FC<Props> = ({ documento, nome }) => {
  const { closeModal } = useModal()
  const { t } = useT()

  const initialValues = {
    prontuario: '',
    empresa: undefined as IOption<number> | undefined,
    nomeMae: '',
    nomeRepresentante: '',
    raca: undefined as IOption<RacaType> | undefined,
    genero: undefined as IOption<GeneroType> | undefined,
    nacionalidade: undefined as IOption<number> | undefined,
    estadoNascimento: undefined as IOption<number> | undefined,
    altura: undefined,
    peso: undefined,
    condicoes: '',
  }

  const formik = useFormik({
    initialValues,
    validationSchema: useMemo(() => updatePacienteSchema(t), [t]),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)

      try {
        const dadosAdicionais: PacienteDtoDadosAdicionais = {
          estadoNascimento: values.estadoNascimento?.value,
          genero: values.genero?.value,
          nacionalidade: values.nacionalidade?.value,
          nomeMae: values.nomeMae || undefined,
          nomeRepresentante: values.nomeRepresentante || undefined,
          raca: values.raca?.value,
        }
        const dadosSaude: PacienteDtoDadosSaude = {
          condicoes: values.condicoes,
          peso: values.peso,
          altura: values.altura,
        }
        const paciente: PacienteDtoAdd = {
          prontuario: values.prontuario,
          tipoDocumento: documento.tipo,
          numeroDocumento: documento.numero,
          dadosAdicionais,
          dadosSaude,
        }
        await vincularPacienteEmpresa(values.empresa?.value as number, paciente)
        showAlert({ title: 'Sucesso!', text: 'Paciente vinculado a instituição.', icon: 'success' })
      } catch (error) {
        showError(error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  return (
    <>
      <div className='mb-10 bg-light-success p-5 rounded'>
        <div className='text-success'>Paciente localizado na base de dados.</div>
      </div>
      <form onSubmit={formik.handleSubmit} noValidate>
        <div className='row mb-10'>
          <Label>Nome: {nome}</Label>
        </div>
        <div className='row mb-10'>
          <Label>Documento: {documento.numero}</Label>
        </div>

        <FormSelect
          formik={formik}
          fetchOptions={OptionsHelper.getOptionsEmpresas}
          name='empresa'
          entity='empresa'
          isClearable={false}
          isRequired
        />
        <CustomAccordion title='Informações adicionais'>
          <div className='row my-5'>
            <h2>Informações Adicionais</h2>
          </div>
          <div className='row mb-3'>
            <div className='col-md-6'>
              <FormInput
                formik={formik}
                name='nomeMae'
                label='Nome da Mãe'
                placeholder='Digite o nome da mãe'
              />
            </div>
            <div className='col-md-6'>
              <FormInput
                formik={formik}
                name='nomeRepresentante'
                label='Nome do representante'
                placeholder='Digite o nome do representante'
              />
            </div>
          </div>
          <div className='row mb-3'>
            <div className='col-md-6'>
              <FormSelect
                formik={formik}
                name='raca'
                label='Raça/Cor'
                placeholder='Selecione uma raça'
                fetchOptions={OptionsHelper.getOptionsRacas}
                isDisabled={formik.isSubmitting}
              />
            </div>
            <div className='col-md-6'>
              <FormSelect
                formik={formik}
                name='genero'
                label='Gênero'
                placeholder='Selecione um gênero'
                fetchOptions={OptionsHelper.getOptionsGeneros}
                isDisabled={formik.isSubmitting}
              />
            </div>
          </div>
          <div className='row mb-3'>
            <div className='col-md-6'>
              <FormSelect
                formik={formik}
                name='nacionalidade'
                label='Nacionalidade'
                placeholder='Selecione uma nacionalidade'
                fetchOptions={OptionsHelper.getOptionsNacionalidades}
                isDisabled={formik.isSubmitting}
              />
            </div>
            <div className='col-md-6'>
              <FormSelect
                formik={formik}
                name='estadoNascimento'
                label='Estado do Nascimento'
                placeholder='Selecione um estado'
                fetchOptions={OptionsHelper.getOptionsEstadosUfNascimento}
                isDisabled={formik.isSubmitting}
              />
            </div>
          </div>
          <div className='row mb-3'>
            <div className='col-md-6'>
              <FormInput
                formik={formik}
                name='peso'
                label='Peso'
                placeholder='Digite um peso'
                isDisabled={formik.isSubmitting}
                type='number'
                infoText='Peso em kg'
              />
            </div>
            <div className='col-md-6'>
              <FormInput
                formik={formik}
                name='altura'
                label='Altura'
                placeholder='Digite uma altura'
                isDisabled={formik.isSubmitting}
                type='number'
                infoText='Altura em metros'
              />
            </div>
          </div>
          <FormTextArea
            formik={formik}
            name='condicoes'
            label='Condições de saúde do paciente'
            placeholder='Digite as condições de saúde do paciente'
            isDisabled={formik.isSubmitting}
          />
        </CustomAccordion>

        <div className='d-flex justify-content-center mt-10'>
          <Button type={ButtonType.Reset} className='btn-secondary me-3' onClick={closeModal}>
            {t('common.cancel')}
          </Button>
          <Button type={ButtonType.Submit} className='btn-primary'>
            {formik.isSubmitting ? <LoadingIndicator /> : t('common.save')}
          </Button>
        </div>
      </form>
    </>
  )
}
